import {
    Button,
    ButtonGroup,
    DateRangePicker,
    DateRangePickerProps,
    Form,
    FormInputProps,
    Icon,
    InputProps,
    Link,
} from '@servicetitan/design-system';
import { InputFieldState } from '@servicetitan/form-state';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, SyntheticEvent, useCallback, useMemo } from 'react';
import { DateRangeFieldState, SelectOption, SingleSelectFieldState } from '../../../utils/form';
import { FilterTag } from '../../filter-tag/filter-tag';
import { PortalSelect, PortalSelectProps } from '../select';
import * as Styles from './filter-fields.module.less';

const useClearLabel = (isEmpty: boolean, mobile: boolean, clear: () => void) =>
    useMemo(
        () =>
            !isEmpty && mobile
                ? {
                      action: (
                          <Link primary onClick={clear}>
                              Clear
                          </Link>
                      ),
                  }
                : undefined,
        [isEmpty, mobile, clear]
    );

export const PortalFilterFieldText: FC<{
    label?: string;
    mobile?: boolean;
    className?: string;

    state: InputFieldState<string>;
    input?: Partial<InputProps>;
}> = observer(({ className, input, label, mobile, state }) => {
    const clear = useCallback(() => state.onChange(''), [state]);
    const icon = useMemo(
        () => (state.value && !mobile ? { name: 'close', size: 16, onClick: clear } : undefined),
        [state.value, mobile, clear]
    );
    const labelProps = useClearLabel(!state.value, !!mobile, clear);

    return (
        <Form.Input
            label={label}
            {...(input ?? {})}
            className={classNames(Styles.itemInput, className, input?.className)}
            labelProps={labelProps}
            icon={icon}
            value={state.value}
            onChange={state.onChangeHandler}
        />
    );
});

interface PortalFilterFieldTextSearchProps extends FormInputProps {
    mobile: boolean;
}

export const PortalFilterFieldTextSearch: FC<PortalFilterFieldTextSearchProps> = observer(props => {
    const { className, mobile, onChange, value, ...otherProps } = props;

    const clearSearch = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation();
            onChange(event, { value: '' });
        },
        [onChange]
    );

    return (
        <Form.Input
            shortLabel={<Icon name="search" />}
            icon={
                value?.trim() ? (
                    <Icon name="close" onClick={clearSearch} type="button" />
                ) : undefined
            }
            maxLength="1024"
            placeholder="Search"
            value={value}
            onChange={onChange}
            className={classNames(
                Styles.searchInput,
                {
                    [Styles.searchInputMobile]: mobile,
                },
                className
            )}
            {...otherProps}
        />
    );
});

interface PortalFilterFieldDateRangeProps extends DateRangePickerProps {
    label?: string;
    mobile?: boolean;
    className?: string;
    state: DateRangeFieldState;
}

export const PortalFilterFieldDateRange: FC<PortalFilterFieldDateRangeProps> = observer(
    ({ className, label, mobile, state, ...dateRangePickerProps }) => {
        const clear = useCallback(() => state.onChange(undefined), [state]);
        const labelProps = useClearLabel(!state.value, !!mobile, clear);

        return (
            <Form.Field label={label} labelProps={labelProps} className={className}>
                <DateRangePicker
                    value={state.value}
                    onChange={state.onChangeHandler}
                    {...dateRangePickerProps}
                />
            </Form.Field>
        );
    }
);

export const PortalFilterFieldSelect: FC<{
    label?: string;
    mobile?: boolean;
    className?: string;

    state: PortalSelectProps<string | number, any>['state'];
    select?: Partial<Omit<PortalSelectProps<string | number, any>, 'state' | 'mobile'>>;
}> = observer(({ className, label, mobile, select, state }) => {
    const isEmptyValue = !state.isSet;
    const clear = useCallback(() => state.onChange(undefined), [state]);
    const labelProps = useClearLabel(isEmptyValue, !!mobile, clear);

    return (
        <Form.Field label={label} labelProps={labelProps} className={className}>
            <PortalSelect
                title={label ?? 'Select'}
                mobile={!!mobile}
                state={state}
                {...(select ?? {})}
                className={classNames(Styles.itemSelectField, select?.className)}
                popover={{ width: '100', ...(select?.popover ?? {}) }}
                trigger={{ clearable: !mobile && !isEmptyValue, ...(select?.trigger ?? {}) }}
            />
        </Form.Field>
    );
});

export const PortalFilterFieldButtonToggle: FC<{
    label?: string;
    mobile?: boolean;
    className?: string;

    state: SingleSelectFieldState<any, any>;
}> = observer(({ className, label, state }) => {
    return (
        <Form.Field label={label} className={classNames(Styles.itemButtonToggle, className)}>
            <ButtonGroup attached fullWidth>
                {state.flatOptions.map(opt => (
                    <Button
                        key={opt.value ?? 'empty'}
                        primary={opt.value === state.value}
                        onClick={() => state.onChangeHandler(opt)}
                        fill="outline"
                    >
                        {opt.text}
                    </Button>
                ))}
            </ButtonGroup>
        </Form.Field>
    );
});

export const PortalFilterFieldTags: FC<{
    label?: string;
    mobile?: boolean;
    className?: string;

    state: PortalSelectProps<any, any>['state'];
    emptyTag?: string;
}> = observer(({ className, emptyTag, label, state }) => {
    const isSelected = (opt: SelectOption<any, any>) => {
        return !state.multiple
            ? state.value === opt.value
            : opt.value === undefined
              ? state.value === undefined || !state.value.length
              : state.value?.includes?.(opt.value);
    };

    const onChange = (opt: SelectOption<any, any>) => {
        if (!state.multiple) {
            state.onChangeHandler(opt);
        } else if (opt.value === undefined) {
            state.onChangeHandler(undefined);
        } else {
            let values = ((state.value ?? []) as any[]).map(v => v);

            if (values.includes(opt.value)) {
                values = values.filter(v => v !== opt.value);
            } else {
                values.push(opt.value);
            }

            state.onChange(values.length ? values : undefined);
        }
    };

    const options = state.flatOptions;

    return (
        <Form.Field label={label} className={className}>
            <div className={Styles.minusMarginTop}>
                {!!emptyTag && (
                    <FilterTag
                        active={state.value === undefined}
                        onClick={() => state.onChangeHandler(undefined)}
                        className="m-r-1 m-t-1"
                    >
                        {emptyTag}
                    </FilterTag>
                )}

                {options.map((opt, index) => (
                    <FilterTag
                        key={opt.value ?? 'empty'}
                        active={isSelected(opt)}
                        onClick={() => onChange(opt)}
                        className={classNames('m-t-1', { 'm-r-1': index !== options.length - 1 })}
                    >
                        {opt.text}
                    </FilterTag>
                ))}
            </div>
        </Form.Field>
    );
});
